"use client";

import { useEffect, type ReactNode } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { northProposal } from "../data/north";
import { PROPOSAL_FONTS_HREF, proposalDisplay, proposalFont } from "../proposalTheme";

const ink = "#241F1A";
const cream = "#F7F4ED";
const paper = "#EFE8DC";
const gold = "#8C6239";
const white = "#FFFFFF";
const muted = "#5C564E";

const FLOW = [
  { time: "45 min", title: "Food and drinks", body: "Guests arrive, mingle, and enjoy food and drinks." },
  { time: "1 hour", title: "The show", body: "Three comics and a host." },
  { time: "45 min", title: "Afterwards", body: "Time to hang out and wrap up the evening." },
];

const INCLUDED = [
  "Private Midtown loft venue",
  "Three comics and a host",
  "Full comedy show production",
  "Food and beverages",
  "Event staffing and coordination",
  "ComedyUO handling the production and logistics",
];

const LOGOS = [
  { src: "https://storage.googleapis.com/cuo-website/JP+Morgan+Logo+(1).png", alt: "J.P. Morgan", large: true },
  { src: "/images/private-events-BILT_Rewards_Logo.png", alt: "BILT" },
  { src: "/images/private-events-Polymarket.png", alt: "Polymarket" },
  { src: "https://storage.googleapis.com/cuo-website/Akin+Gump+Logo.jpg", alt: "Akin Gump" },
  { src: "/images/private-events-Proskauer_Rose_Logo.png", alt: "Proskauer" },
  { src: "/images/private-events-Skadden_Arps_Logo.png", alt: "Skadden" },
  { src: "/images/private-events-Norton_Rose_Fulbright_Logo.png", alt: "Norton Rose Fulbright" },
];

export default function NorthProposalPage() {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${PROPOSAL_FONTS_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PROPOSAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);

  return (
    <Box component="main" sx={{ bgcolor: cream, color: ink, minHeight: "100vh" }}>
      <Helmet>
        <title>North · A Private Comedy Night | ComedyUO</title>
        <meta
          name="description"
          content="A private year-end comedy party for North. A Midtown loft, food and drinks, and a workplace-appropriate comedy show for 40 people."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta property="og:title" content="North · A Private Comedy Night | ComedyUO" />
        <meta
          property="og:description"
          content="A private comedy night for North. Your team, a great space, and a night worth getting together for."
        />
        <meta property="og:image" content={northProposal.heroImage} />
        <meta property="og:image:alt" content={northProposal.heroImageAlt} />
      </Helmet>

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          px: { xs: 3, md: 6 },
          height: { xs: 64, md: 76 },
          bgcolor: ink,
        }}
      >
        <Box component="img" src={northProposal.comedyuoLogo} alt="ComedyUO" sx={{ height: { xs: 28, md: 36 }, width: "auto" }} />
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontSize: { xs: "0.68rem", md: "0.78rem" },
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: white,
            textAlign: "right",
          }}
        >
          North
        </Typography>
      </Box>

      <Box sx={{ position: "relative", minHeight: { xs: "72vh", md: "82vh" }, display: "flex", alignItems: "flex-end" }}>
        <Box
          component="img"
          src={northProposal.heroImage}
          alt={northProposal.heroImageAlt}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 40%" }}
        />
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(36,31,26,0.12) 0%, rgba(36,31,26,0.84) 100%)" }} />
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 1120, mx: "auto", px: { xs: 3, md: 6 }, pb: { xs: 6, md: 9 }, pt: 12 }}>
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ink,
              bgcolor: white,
              display: "inline-block",
              px: 1.5,
              py: 0.75,
              mb: 2.5,
            }}
          >
            Prepared for Christina Fursich · North
          </Typography>
          <Typography
            component="h1"
            sx={{ fontFamily: proposalDisplay, fontWeight: 500, color: white, fontSize: { xs: "2.7rem", md: "4.5rem" }, lineHeight: 1.02, maxWidth: 820, mb: 2 }}
          >
            A Private Comedy Night for North
          </Typography>
          <Typography sx={{ fontFamily: proposalFont, color: "rgba(255,255,255,0.92)", fontSize: { xs: "1.1rem", md: "1.35rem" }, maxWidth: 640, lineHeight: 1.45, mb: 2.5 }}>
            Your team. A great space. A night worth getting together for.
          </Typography>
          <Typography sx={{ fontFamily: proposalFont, color: "rgba(255,255,255,0.82)", fontSize: { xs: "1rem", md: "1.08rem" }, maxWidth: 620, lineHeight: 1.6 }}>
            ComedyUO produces intimate comedy experiences featuring incredible comedians in some of New York City's most unexpected spaces. For North, we're bringing that experience to a private company celebration.
          </Typography>
        </Box>
      </Box>

      <Section>
        <Eyebrow>Your evening</Eyebrow>
        <Heading>An intimate night for 40.</Heading>
        <Body>
          An intimate, fully produced comedy night for approximately 40 North employees in a Midtown Manhattan loft. The evening brings together a private venue, food and drinks, and an incredible live comedy show.
        </Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          {FLOW.map((step) => (
            <Box key={step.title} sx={{ bgcolor: white, borderTop: `3px solid ${gold}`, px: 2.5, py: 2.5 }}>
              <Typography sx={{ fontFamily: proposalFont, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.14em", color: gold, mb: 1 }}>{step.time}</Typography>
              <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.6rem", md: "1.9rem" }, lineHeight: 1.1, mb: 1 }}>{step.title}</Typography>
              <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "1.02rem", lineHeight: 1.55 }}>{step.body}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "0.95rem", mt: 3, maxWidth: 640 }}>
          Two and a half hours once the night starts. The start time is set once a date is chosen.
        </Typography>
      </Section>

      <Section>
        <Eyebrow>The venue</Eyebrow>
        <Heading>Your Own Private Comedy Club.</Heading>
        <Body>
          A Midtown loft for approximately 40 guests. Private to North. Room for food, drinks, and a live show. A more memorable night than a typical company dinner.
        </Body>
        <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: { xs: "1.05rem", md: "1.15rem" }, lineHeight: 1.6, maxWidth: 720, mb: 4 }}>
          The show is an hour: three comics and a host. The comedy is corporate friendly. We book comics with observational humor, in the spirit of Jim Gaffigan, Jerry Seinfeld, and Nate Bargatze. We coordinate with you on a no-no list, and we tell the comics to steer clear of topics you'd like to avoid.
        </Typography>
        <Box sx={{ bgcolor: white, border: "1px solid rgba(36,31,26,0.12)" }}>
          <Box
            component="img"
            src="https://storage.googleapis.com/cuo-website/Rooms%20We've%20Fooled%20Magic%20Show.jpg"
            alt="A representative Midtown room ComedyUO has used for a private show"
            sx={{ width: "100%", height: { xs: 240, md: 460 }, objectFit: "cover", display: "block" }}
          />
          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Typography sx={{ fontFamily: proposalFont, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, mb: 1 }}>
              Midtown Manhattan · Representative room
            </Typography>
            <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.8rem", md: "2.2rem" }, lineHeight: 1.1, mb: 1.25 }}>
              The loft, still being confirmed
            </Typography>
            <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "1.05rem", lineHeight: 1.6, maxWidth: 680 }}>
              This photograph is from a Midtown room ComedyUO has already produced in. It shows the kind of space we have discussed with Christina. It is not a confirmation that this specific loft is booked.
            </Typography>
          </Box>
        </Box>
      </Section>

      <Section tone="paper">
        <Eyebrow>Included</Eyebrow>
        <Heading>Your Private North Comedy Experience</Heading>
        <Body>
          ComedyUO produces the night. Venue, comedy, food, staffing, and the logistics sit with us, so North has one team to work with.
        </Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 4 }}>
          {INCLUDED.map((item) => (
            <Box key={item} sx={{ bgcolor: white, borderTop: `3px solid ${gold}`, px: 2.5, py: 2.25 }}>
              <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.35rem", md: "1.55rem" }, lineHeight: 1.2 }}>{item}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
          <Box component="img" src="https://storage.googleapis.com/cuo-website/Screenshot%202026-10-27%20at%209.04.59%E2%80%AFPM.png" alt="Food from a ComedyUO night" sx={{ width: "100%", height: { xs: 220, md: 280 }, objectFit: "cover", display: "block" }} />
          <Box component="img" src="https://storage.googleapis.com/cuo-website/Screenshot%202026-10-27%20at%209.05.48%E2%80%AFPM.png" alt="Food from a ComedyUO night" sx={{ width: "100%", height: { xs: 220, md: 280 }, objectFit: "cover", display: "block" }} />
        </Box>
        <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "1rem", lineHeight: 1.6, maxWidth: 720 }}>
          The bar is confirmed with you. Beer and wine is one option. A full open bar, including liquor, is the other. Liquor is not included in the estimate until that choice and the pricing are confirmed. The photos are from other ComedyUO nights, not a final North menu.
        </Typography>
      </Section>

      <Section>
        <Eyebrow>ComedyUO</Eyebrow>
        <Heading>Hundreds of nights in New York.</Heading>
        <Body>
          ComedyUO has produced hundreds of live comedy and magic experiences across New York City, with professional comedians and rooms that do not look like a hotel ballroom.
        </Body>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 3 }, alignItems: "center", mb: 4 }}>
          {LOGOS.map((logo) => (
            <Box
              key={logo.alt}
              component="img"
              src={logo.src}
              alt={logo.alt}
              sx={{
                height: logo.large ? { xs: 78, md: 108 } : { xs: 36, md: 46 },
                width: "auto",
                maxWidth: logo.large ? 340 : 168,
                objectFit: "contain",
                mixBlendMode: "multiply",
              }}
            />
          ))}
        </Box>
        <Box
          component="img"
          src="/images/Economy_Candy_Crowd.jpg"
          alt="Guests at a ComedyUO private show"
          sx={{ width: "100%", height: { xs: 220, md: 360 }, objectFit: "cover", display: "block" }}
        />
      </Section>

      <Section tone="paper">
        <Eyebrow>Next</Eyebrow>
        <Heading>Let's Make It a Night to Remember.</Heading>
        <Body>
          We'd love to bring North together for an unforgettable night of comedy. Once we've settled on a date and the food and beverage details, we can take care of the rest.
        </Body>
        <Typography sx={{ fontFamily: proposalDisplay, fontSize: "1.7rem", mb: 0.5 }}>{northProposal.contactName}</Typography>
        <Typography sx={{ fontFamily: proposalFont, color: muted, mb: 2 }}>{northProposal.contactRole}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Link
            href={`mailto:${northProposal.contactEmail}?subject=${encodeURIComponent("North private comedy night")}`}
            underline="none"
            sx={{ fontFamily: proposalFont, fontWeight: 700, color: white, bgcolor: ink, px: 2, py: 1.15 }}
          >
            {northProposal.contactEmail}
          </Link>
          <Link href="tel:+13104698961" underline="none" sx={{ fontFamily: proposalFont, fontWeight: 700, color: ink, border: `1px solid ${ink}`, px: 2, py: 1.15 }}>
            {northProposal.contactPhone}
          </Link>
        </Box>
      </Section>

      <Section tone="ink">
        <Eyebrow onDark>Investment</Eyebrow>
        <Heading onDark>One evening. One number.</Heading>
        <Body onDark>
          A single package for the night, not a menu of tiers. The final details are confirmed once the date, the loft, the food and beverage selection, and the production specs are set. This is an estimate, not a finalized quote.
        </Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, borderTop: "1px solid rgba(255,255,255,0.18)", pt: 4 }}>
          <Box>
            <Typography sx={{ fontFamily: proposalFont, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 1 }}>
              Estimated all-in
            </Typography>
            <Typography sx={{ fontFamily: proposalDisplay, color: white, fontSize: { xs: "3.2rem", md: "4.2rem" }, lineHeight: 1 }}>$10,000</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: proposalFont, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 1 }}>
              Guests
            </Typography>
            <Typography sx={{ fontFamily: proposalDisplay, color: white, fontSize: { xs: "3.2rem", md: "4.2rem" }, lineHeight: 1 }}>40</Typography>
          </Box>
        </Box>
      </Section>

      <Box sx={{ px: { xs: 3, md: 6 }, py: 3, bgcolor: ink }}>
        <Typography sx={{ fontFamily: proposalFont, fontSize: "0.85rem", color: "rgba(255,255,255,0.75)" }}>
          Prepared by ComedyUO · Confidential · North
        </Typography>
      </Box>
    </Box>
  );
}

function Section({ children, tone = "cream" }: { children: ReactNode; tone?: "cream" | "paper" | "ink" }) {
  const bg = tone === "ink" ? ink : tone === "paper" ? paper : cream;
  return (
    <Box component="section" sx={{ bgcolor: bg, px: { xs: 3, md: 6 }, py: { xs: 7, md: 10 } }}>
      <Box sx={{ maxWidth: 1120, mx: "auto" }}>{children}</Box>
    </Box>
  );
}

function Eyebrow({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: onDark ? "rgba(255,255,255,0.7)" : gold,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function Heading({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: proposalDisplay,
        fontWeight: 500,
        fontSize: { xs: "2.1rem", md: "3rem" },
        lineHeight: 1.05,
        color: onDark ? white : ink,
        mb: 2,
        maxWidth: 780,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: { xs: "1.05rem", md: "1.15rem" },
        lineHeight: 1.6,
        color: onDark ? "rgba(255,255,255,0.82)" : muted,
        maxWidth: 720,
        mb: 4,
      }}
    >
      {children}
    </Typography>
  );
}
